import { useState } from 'react'
import { Pill } from './primitives'

const PRICE = 20
const FREE_SEATS = 1 // owner's first extra teammate is free

const INCLUDED = [
  'Unified casting inbox (email + forwarding)',
  'Unlimited clients & auditions',
  'Branded self-tape vault — kept permanently',
  'Self-tape review: retakes, reorder, release',
  'Live audition → booking journey',
  'Client portal (mobile-first, installable)',
  'Roster & casting-director analytics',
  'Shareable talent packages with view tracking',
  'Granular team roles & per-client assignments',
  'Push & email notifications',
]

/** RepTree vs. the usual patchwork — the narrative the page has been building. */
const COMPARE: { label: string; reptree: boolean; old: string }[] = [
  { label: 'One inbox for every casting source', reptree: true, old: '5 logins + your email' },
  { label: 'Self-tapes kept forever, searchable', reptree: true, old: 'WeTransfer links expire' },
  { label: 'Clients see live status themselves', reptree: true, old: 'A dozen "any news?" texts' },
  { label: 'Pipeline analytics & conversion', reptree: true, old: 'A spreadsheet, maybe' },
  { label: 'Branded to your agency', reptree: true, old: 'Someone else’s logo' },
]

export function PricingTable() {
  const [seats, setSeats] = useState(1)
  const billable = Math.max(0, seats - FREE_SEATS)
  const total = billable * PRICE

  return (
    <div className="grid items-start gap-8 md:grid-cols-[1fr_1.1fr]">
      {/* Plan + seat calculator */}
      <div className="rounded-card border bg-[color:rgb(var(--bg-elev))] p-8 hairline">
        <Pill>One simple plan</Pill>
        <div className="mt-5 flex items-end gap-1.5">
          <span className="font-display text-6xl leading-none">${PRICE}</span>
          <span className="mb-1 text-[color:rgb(var(--muted))]">/ seat / month</span>
        </div>
        <p className="mt-3 text-[color:rgb(var(--muted))]">First month free · your first extra teammate is free · cancel anytime.</p>

        <div className="mt-7">
          <div className="flex items-center justify-between">
            <span className="label-mono text-[color:rgb(var(--muted))]">Team size</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSeats((s) => Math.max(1, s - 1))}
                className="grid h-8 w-8 place-items-center rounded-nav border hairline disabled:opacity-40"
                disabled={seats <= 1}
                aria-label="Remove seat"
              >
                −
              </button>
              <span className="w-6 text-center font-display text-xl">{seats}</span>
              <button
                onClick={() => setSeats((s) => Math.min(50, s + 1))}
                className="grid h-8 w-8 place-items-center rounded-nav border hairline"
                aria-label="Add seat"
              >
                +
              </button>
            </div>
          </div>
          <div className="row-rule mt-5 flex items-end justify-between pt-5">
            <div>
              <div className="font-display text-4xl">${total}<span className="text-base text-[color:rgb(var(--muted))]"> / mo</span></div>
              <div className="label-mono mt-1 text-[color:rgb(var(--muted))]">
                {billable === 0 ? 'free — owner + 1 teammate' : `${billable} billable seat${billable > 1 ? 's' : ''}`}
              </div>
            </div>
            <a href="/login?mode=signup" className="btn-brass btn-lg">Start free<span className="cta-arrow">→</span></a>
          </div>
        </div>
      </div>

      {/* What's included */}
      <div>
        <h3 className="font-display text-2xl">Everything’s included.</h3>
        <p className="mt-1 text-[color:rgb(var(--muted))]">No add-on tiers, no per-feature upsells.</p>
        <div className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {INCLUDED.map((f) => (
            <div key={f} className="flex items-start gap-2.5">
              <span className="feature-check mt-0.5 !h-6 !w-6" aria-hidden>
                <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none">
                  <path d="M3 8.5l3.2 3.2L13 4.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="text-sm">{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison */}
      <div className="md:col-span-2">
        <div className="overflow-hidden rounded-card border hairline">
          <div className="grid grid-cols-[1.6fr_1fr_1fr] bg-[color:rgb(var(--tint-1))] px-5 py-3 label-mono text-[color:rgb(var(--muted))]">
            <span>What you get</span>
            <span className="text-center text-[color:rgb(var(--brand-primary))]">RepTree</span>
            <span className="text-center">The patchwork</span>
          </div>
          {COMPARE.map((r) => (
            <div key={r.label} className="row-rule grid grid-cols-[1.6fr_1fr_1fr] items-center px-5 py-3.5">
              <span className="text-sm">{r.label}</span>
              <span className="flex justify-center">
                <span className="feature-check !h-6 !w-6" aria-hidden>
                  <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none">
                    <path d="M3 8.5l3.2 3.2L13 4.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </span>
              <span className="text-center text-sm text-[color:rgb(var(--muted))]">{r.old}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
